import { useNavigate, useParams } from "react-router";
import { useDisclosure } from "@/components/ui/Disclosure";
import { StatusPill } from "@/components/ui/StatusPill";
import { FoldingCard } from "@/components/patterns/FoldingCard";
import { StepCard } from "@/components/patterns/StepCard";
import { useInventionElements } from "@/hooks/use-invention";
import { useNoveltyGate } from "@/hooks/use-novelty-gate";
import { usePositions } from "@/hooks/use-positions";
import { useTask } from "@/hooks/use-tasks";
import { stageHeadingLabel, tabPath } from "@/v2/stage-vocabulary";
import { StagePage } from "../../layout/StagePage";
import { StageFooter } from "../../layout/StageFooter";
import { priorArtOf } from "../prior-art-list";
import { mappableElements } from "./element-mapping";
import { PositionsPanel } from "./PositionsPanel";
import { StepBanner } from "../../notifications/StepChanges";
import { WhatIsAPosition } from "./WhatIsAPosition";
import { StageSources } from "@/v2/layout/StageSources";

/** The condition the gate names while there is no position to assess. */
const NO_POSITION = {
  id: "positions",
  outstanding: true,
  first_id: null,
  label: "Add or generate at least one position to continue.",
};

/**
 * What every position must rest on, shut until the attorney opens it: only the
 * numbered elements, since a position cannot rest on one the invention has not
 * taken in.
 */
function LockedElements({ taskId }: { taskId: string }) {
  const fold = useDisclosure();
  const { data: invention } = useInventionElements(taskId);
  const elements = mappableElements(invention);
  const count =
    elements.length === 1
      ? "this 1 element"
      : `these ${elements.length} elements`;
  return (
    <FoldingCard
      fold={fold}
      testId="locked-elements"
      className="mt-6"
      title="Locked invention elements"
      meta={
        invention === undefined
          ? undefined
          : `Every position must map to ${elements.length === 1 ? "" : "one or more of "}${count}.`
      }
    >
      <ul className="mt-3 flex flex-wrap gap-2">
        {elements.map((element) => (
          <li key={element.element_id}>
            <StatusPill tone="neutral">
              <b>Element {element.number}</b>
              {element.text}
            </StatusPill>
          </li>
        ))}
      </ul>
    </FoldingCard>
  );
}

/**
 * The gate onto the library, while no current run exists: it opens on one
 * position, confirmed or not, since positions are confirmed inside the
 * analysis. Once a run is current the work has moved there, and the gate is
 * not drawn.
 */
function PositionsFooter({ taskId }: { taskId: string }) {
  const navigate = useNavigate();
  const { data: task } = useTask(taskId);
  const { data: served } = usePositions(taskId);
  const positions = served?.positions ?? [];
  const { data: gate } = useNoveltyGate(
    taskId,
    priorArtOf(task?.files),
    positions,
  );
  // Only a completed run that is still current moves the work to the analysis.
  const current =
    gate !== undefined &&
    gate.analysis.status === "ready" &&
    !gate.analysis.stale;
  // The work has moved on to the analysis, but a change to a position still
  // has its banner kept in view here.
  if (current)
    return <StepBanner taskId={taskId} step="define_positions" alone />;
  const drafted = positions.length > 0;
  return (
    <StageFooter
      testId="novelty-positions-footer"
      heading={drafted ? "Positions drafted" : "Add a position to continue"}
      detail={
        drafted
          ? "Next, settle the prior art library the analysis runs against."
          : "The novelty analysis runs against the positions you draft here."
      }
      ready={drafted}
      label="Continue to Add Prior Art"
      pendingLabel="Continue to Add Prior Art"
      onSubmit={() =>
        void navigate(tabPath(taskId, "thesis", "novelty", "prior-art"))
      }
      sticky
      above={<StepBanner taskId={taskId} step="define_positions" />}
      conditions={drafted ? [] : [NO_POSITION]}
    />
  );
}

/**
 * The novelty page's first tab: the positions the analysis will assess,
 * drafted against the locked invention. Nothing is confirmed here; each
 * position is confirmed inside the analysis.
 */
export function DefinePositionsPane() {
  const { taskId } = useParams<{ taskId: string }>();
  if (taskId === undefined) return null;
  return (
    <StagePage
      measure="thesis"
      guide="prior-art-and-positions"
      testId="novelty-positions-pane"
      taskId={taskId}
      eyebrow={`${stageHeadingLabel("thesis")} · Define Positions`}
      title="Draft the positions"
      lede="The invention is locked. Settle your positions, then run the analysis. Every position is checked against the whole prior art library."
    >
      <StageSources taskId={taskId} stage="novelty" />
      <LockedElements taskId={taskId} />
      <StepCard
        number={1}
        title="Define Positions"
        intro="Amend, add or drop positions, then run the analysis against the prior art library."
        beside={<WhatIsAPosition />}
      >
        <PositionsPanel taskId={taskId} />
      </StepCard>
      <PositionsFooter taskId={taskId} />
    </StagePage>
  );
}
